import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import api, { fileUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Star } from "lucide-react";
import { Link } from "react-router-dom";

export default function CoachDashboard() {
  const [pending, setPending] = useState([]);
  const [rate, setRate] = useState({ open: false, player: null, rating: 7, report: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/pending");
      setPending(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (id) => {
    try {
      await api.post(`/coach/verify/${id}`);
      toast.success("Player verified");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitRating = async () => {
    try {
      await api.post(`/coach/rate/${rate.player.user_id}`, { rating: rate.rating, report: rate.report });
      toast.success("Report submitted");
      setRate({ open: false, player: null, rating: 7, report: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "bg-transparent border-white/20 rounded-sm h-11 focus:border-[#e65f2b]";

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">Coach dashboard</div>
        <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">Pending verifications</h1>

        <div className="mt-6 border border-white/10 bg-[#141414] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/60 text-xs uppercase tracking-widest text-white/60">
              <tr>
                <th className="text-left p-4">Player</th>
                <th className="text-left p-4">Position</th>
                <th className="text-left p-4">Country</th>
                <th className="text-left p-4">Age</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.id || p.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`pending-row-${p.user_id}`}>
                  <td className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-black border border-white/10 overflow-hidden">
                      {p.photo_path ? <img src={fileUrl(p.photo_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30 font-display">{(p.name || "?")[0]}</div>}
                    </div>
                    <div>
                      <div className="font-bold">{p.name}</div>
                      <div className="text-xs text-white/50">{p.academy || "No academy listed"}</div>
                    </div>
                  </td>
                  <td className="p-4">{p.position || "—"}</td>
                  <td className="p-4">{p.country || "—"}</td>
                  <td className="p-4">{p.age || "—"}</td>
                  <td className="p-4 text-right space-x-2">
                    <Link to={`/players/${p.user_id}`}>
                      <Button variant="outline" size="sm" className="border-white/20 hover:bg-white/10 rounded-sm text-xs uppercase" data-testid={`coach-view-${p.user_id}`}>View</Button>
                    </Link>
                    <Dialog open={rate.open && rate.player?.user_id === p.user_id} onOpenChange={(o) => setRate((s) => ({ ...s, open: o, player: o ? p : null }))}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-[#eab308]/50 text-[#eab308] hover:bg-[#eab308]/10 rounded-sm text-xs uppercase" data-testid={`coach-rate-${p.user_id}`}>
                          <Star size={12} className="mr-1" />Rate
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white">
                        <DialogHeader>
                          <DialogTitle className="font-display uppercase text-2xl">Scouting report</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs uppercase tracking-widest text-white/70 mb-2">Rating (1-10): <span className="text-[#eab308]">{rate.rating}</span></div>
                            <input type="range" min="1" max="10" value={rate.rating} onChange={(e) => setRate({ ...rate, rating: parseInt(e.target.value) })} className="w-full accent-[#e65f2b]" data-testid="rate-slider" />
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-widest text-white/70 mb-2">Report</div>
                            <Textarea value={rate.report} onChange={(e) => setRate({ ...rate, report: e.target.value })} className={`${inputCls} min-h-32`} data-testid="rate-report-input" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={submitRating} className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase rounded-sm" data-testid="rate-submit-btn">Submit report</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Button onClick={() => verify(p.user_id)} size="sm" className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black rounded-sm text-xs uppercase font-bold" data-testid={`coach-verify-${p.user_id}`}>
                      <ShieldCheck size={12} className="mr-1" />Verify
                    </Button>
                  </td>
                </tr>
              ))}
              {pending.length === 0 && (
                <tr><td colSpan="5" className="p-6 text-center text-white/50">No pending verifications.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
